<div class="tab-body">
  <div class="tab-head">
    @if (singleMode()) {
      <div>
        <h3>Callbacks</h3>
        <p>Result webhooks we call on this client's URL when an event fires (e.g. payment.success).</p>
      </div>
    } @else {
      <div class="cb-filter">
        <label>Client</label>
        <p-select [options]="clientOptions()" optionLabel="label" optionValue="value"
          [showClear]="true" [filter]="true" placeholder="All clients" appendTo="body"
          [ngModel]="filterClientId()" (ngModelChange)="onFilterClient($event)" />
      </div>
    }
    <div class="head-actions">
      <p-button label="View Logs" icon="pi pi-history" [outlined]="true" (onClick)="openLogs()" />
      <p-button label="Add Callback" icon="pi pi-plus" (onClick)="openAdd()" />
    </div>
  </div>

  <p-table [value]="rows()" [loading]="loading()" dataKey="id" [rows]="10"
    [paginator]="rows().length > 10" [rowsPerPageOptions]="[10, 25, 50]" styleClass="p-datatable-sm" [showCurrentPageReport]="true" currentPageReportTemplate="Showing {first}–{last} of {totalRecords}">
    <ng-template pTemplate="header">
      <tr>
        @if (!singleMode()) {
          <th>Client</th>
        }
        <th>Event</th>
        <th>Callback URL</th>
        <th class="ta-c">Method</th>
        <th>Auth Header</th>
        <th class="ta-r">Timeout</th>
        <th class="ta-r">Retries</th>
        <th class="ta-c">Active</th>
        <th class="ta-r">Actions</th>
      </tr>
    </ng-template>
    <ng-template pTemplate="body" let-r>
      <tr>
        @if (!singleMode()) {
          <td class="name">
            {{ r.clientName || '—' }}
            <span class="sub mono">{{ r.clientCode }}</span>
          </td>
        }
        <td><p-tag [value]="r.eventName" severity="info" /></td>
        <td>
          <span class="mono cb-url" [title]="r.callbackUrl">{{ trunc(r.callbackUrl) }}</span>
        </td>
        <td class="ta-c">
          <p-tag [value]="r.httpMethod || 'POST'" [severity]="methodSeverity(r.httpMethod)" />
        </td>
        <td>
          @if (r.headerKey || r.headerValue) {
            <span class="auth-chip"
              [pTooltip]="r.headerValue ? 'An auth header value is configured (hidden)' : 'Header name only — no value set'">
              <i class="pi pi-lock"></i> {{ r.headerKey || 'Header' }}
            </span>
          } @else {
            <span class="muted">—</span>
          }
        </td>
        <td class="ta-r">{{ r.timeoutInSeconds }}s</td>
        <td class="ta-r">{{ r.retryCount }}</td>
        <td class="ta-c">
          <span class="active-toggle" (click)="toggleActive(r)" pTooltip="Click to toggle">
            <p-tag [value]="r.isActive ? 'Active' : 'Inactive'"
              [severity]="r.isActive ? 'success' : 'secondary'" />
          </span>
        </td>
        <td class="ta-r actions">
          <p-button icon="pi pi-pencil" [rounded]="true" [text]="true" severity="secondary"
            pTooltip="Edit" (onClick)="openEdit(r)" />
          <p-button icon="pi pi-history" [rounded]="true" [text]="true" severity="secondary"
            pTooltip="Delivery log" (onClick)="openLogs(r)" />
          <p-button icon="pi pi-trash" [rounded]="true" [text]="true" severity="danger"
            pTooltip="Delete" (onClick)="remove(r)" />
        </td>
      </tr>
    </ng-template>
    <ng-template pTemplate="emptymessage">
      <tr>
        <td [attr.colspan]="singleMode() ? 8 : 9">
          <div class="state"><i class="pi pi-send"></i>
            <p>No callbacks yet. Click "Add Callback" to register one.</p>
          </div>
        </td>
      </tr>
    </ng-template>
  </p-table>
</div>

<!-- ===================== ADD / EDIT MODAL ===================== -->
<p-dialog [visible]="showModal()" (visibleChange)="showModal.set($event)" [modal]="true" [draggable]="true"
  [resizable]="false" [closable]="true" [dismissableMask]="false"
  [header]="editing() ? 'Edit Callback' : 'Add Callback'" styleClass="menu-dialog"
  [style]="{ width: '640px' }" [breakpoints]="{ '640px': '94vw' }">
  <form [formGroup]="form" class="menu-form">
    @if (!singleMode()) {
      <div class="field">
        <label>Client <span class="req">*</span></label>
        <p-select formControlName="clientId" [options]="clientOptions()" optionLabel="label"
          optionValue="value" [filter]="true" placeholder="Select client" appendTo="body" />
        @if (form.get('clientId')?.touched && form.get('clientId')?.invalid) {
          <small class="err">Client is required</small>
        }
      </div>
    }

    <div class="grid-2">
      <div class="field">
        <label>Event <span class="req">*</span></label>
        <p-select formControlName="eventName" [options]="eventOptions" [editable]="true"
          placeholder="payment.success" appendTo="body" />
        @if (form.get('eventName')?.touched && form.get('eventName')?.invalid) {
          <small class="err">Event name is required</small>
        }
      </div>
      <div class="field">
        <label>HTTP Method</label>
        <p-select formControlName="httpMethod" [options]="methodOptions" appendTo="body" />
      </div>
    </div>

    <div class="field">
      <label>Callback URL <span class="req">*</span></label>
      <input pInputText type="url" formControlName="callbackUrl"
        placeholder="https://client.example.com/hooks/arogya" />
      @if (form.get('callbackUrl')?.touched && form.get('callbackUrl')?.invalid) {
        <small class="err">
          @if (form.get('callbackUrl')?.hasError('required')) {
            Callback URL is required
          } @else {
            Must be an absolute http:// or https:// URL
          }
        </small>
      }
      <small class="hint-muted">Prefer https. The same event can post to several URLs — every active
        callback fires; only an exact event + URL duplicate is rejected.</small>
    </div>

    <div class="grid-2">
      <div class="field">
        <label>Auth Type</label>
        <input pInputText type="text" formControlName="authenticationType"
          placeholder="Bearer / ApiKey (informational)" />
      </div>
      <div class="field">
        <label>Header Key</label>
        <input pInputText type="text" formControlName="headerKey"
          placeholder="Authorization or X-Api-Key" />
      </div>
    </div>

    <div class="field">
      <label>Header Value</label>
      <p-password formControlName="headerValue" [toggleMask]="true" [feedback]="false"
        placeholder="Secret sent as the header's value (optional)" autocomplete="new-password" />
      <small class="hint-muted">Treated as a secret — masked in the grid, shown only here.</small>
    </div>

    <div class="grid-2">
      <div class="field">
        <label>Timeout (seconds)</label>
        <p-inputnumber formControlName="timeoutInSeconds" [min]="1" [max]="60"
          [useGrouping]="false" [showButtons]="true" suffix=" s" />
        @if (form.get('timeoutInSeconds')?.touched && form.get('timeoutInSeconds')?.invalid) {
          <small class="err">1–60 seconds</small>
        }
      </div>
      <div class="field">
        <label>Retry Count</label>
        <p-inputnumber formControlName="retryCount" [min]="1" [max]="5" [useGrouping]="false"
          [showButtons]="true" />
        @if (form.get('retryCount')?.touched && form.get('retryCount')?.invalid) {
          <small class="err">1–5 attempts</small>
        }
        <small class="hint-muted">Attempts on non-2xx, linear backoff.</small>
      </div>
    </div>

    <label class="switch-row">
      <p-toggleSwitch formControlName="isActive" />
      <span>Active</span>
    </label>

    <div class="modal-actions">
      <p-button label="Cancel" [outlined]="true" (onClick)="closeModal()" />
      <p-button [label]="saving() ? 'Saving…' : 'Save'" icon="pi pi-check" (onClick)="save()"
        [disabled]="saving()" [loading]="saving()" />
    </div>
  </form>
</p-dialog>

<!-- ===================== DELIVERY LOG MODAL ===================== -->
<p-dialog [visible]="showLogs()" (visibleChange)="showLogs.set($event)" [modal]="true" [draggable]="true"
  [resizable]="false" [closable]="true" [dismissableMask]="true" [header]="logsTitle()"
  styleClass="menu-dialog" [style]="{ width: '1000px' }" [breakpoints]="{ '1200px': '94vw' }">
  <div class="logs-toolbar">
    @if (logsFor()) {
      <p-select [options]="logScopeOptions" optionLabel="label" optionValue="value"
        [ngModel]="logsScope()" (ngModelChange)="onLogsScopeChange($event)" appendTo="body"
        styleClass="logs-scope" />
    }
    <span class="count">{{ logsTotal() }} deliver{{ logsTotal() === 1 ? 'y' : 'ies' }}</span>
    <p-button icon="pi pi-refresh" [rounded]="true" [text]="true" severity="secondary"
      pTooltip="Refresh" (onClick)="loadLogs()" />
  </div>

  <p-table [value]="logsRows()" [loading]="logsLoading()" dataKey="id" styleClass="p-datatable-sm">
    <ng-template pTemplate="header">
      <tr>
        <th class="log-toggle-col"></th>
        <th>When</th>
        <th>Event</th>
        <th>Reference</th>
        <th class="ta-c">Status</th>
        <th class="ta-c">HTTP</th>
        <th class="ta-r">Retries</th>
        <th>Response</th>
      </tr>
    </ng-template>
    <ng-template pTemplate="body" let-l>
      <tr>
        <td class="log-toggle-col">
          <p-button [icon]="isLogExpanded(l.id) ? 'pi pi-chevron-down' : 'pi pi-chevron-right'"
            [rounded]="true" [text]="true" size="small" severity="secondary"
            pTooltip="Request / response payload" (onClick)="toggleLogExpand(l.id)" />
        </td>
        <td class="nowrap">{{ l.createdDate | indDate: true }}</td>
        <td>{{ l.eventName || '—' }}</td>
        <td>
          @if (l.referenceId != null) {
            <span class="mono">{{ l.referenceType || 'ref' }} #{{ l.referenceId }}</span>
          } @else {
            <span class="muted">—</span>
          }
        </td>
        <td class="ta-c">
          <p-tag [value]="l.isSuccess ? 'OK' : 'Failed'"
            [severity]="l.isSuccess ? 'success' : 'danger'"
            [icon]="l.isSuccess ? 'pi pi-check' : 'pi pi-times'" />
        </td>
        <td class="ta-c mono" [pTooltip]="l.httpStatusCode == null ? 'No HTTP response (DNS / timeout / connection error)' : ''">
          {{ l.httpStatusCode ?? '—' }}
        </td>
        <td class="ta-r">{{ l.retryCount ?? 0 }}</td>
        <td>
          <span class="mono cb-url" [title]="l.responseJson">{{ trunc(l.responseJson, 40) }}</span>
        </td>
      </tr>
      @if (isLogExpanded(l.id)) {
        <tr class="log-detail-row">
          <td colspan="8">
            <div class="log-detail">
              @if (l.callbackUrl) {
                <div class="log-json">
                  <label>URL</label>
                  <pre>{{ l.callbackUrl }}</pre>
                </div>
              }
              <div class="log-json">
                <label>Request we sent</label>
                <pre>{{ pretty(l.requestJson) }}</pre>
              </div>
              <div class="log-json">
                <label>Response received</label>
                <pre>{{ pretty(l.responseJson) }}</pre>
              </div>
            </div>
          </td>
        </tr>
      }
    </ng-template>
    <ng-template pTemplate="emptymessage">
      <tr>
        <td colspan="8">
          <div class="state"><i class="pi pi-inbox"></i>
            <p>No deliveries yet.</p>
          </div>
        </td>
      </tr>
    </ng-template>
  </p-table>

  @if (logsTotal() > 0) {
    <p-paginator [rows]="logsPageSize()" [totalRecords]="logsTotal()"
      [first]="(logsPage() - 1) * logsPageSize()" [rowsPerPageOptions]="[10, 25, 50]"
      [showCurrentPageReport]="true" currentPageReportTemplate="Showing {first}–{last} of {totalRecords}"
      (onPageChange)="onLogsPage($event)" />
  }
</p-dialog>
